/* eslint-disable no-unused-vars */
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowBackIos as BackIcon,
  SaveOutlined as SaveIcon,
  ModeEditOutlineOutlined as EditIcon,
} from '@mui/icons-material';
import {
  Box,
  TextField,
  Divider,
  Stack,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import { Toast } from 'app/components';
import { Loader } from 'app/components/v2';
import PrivacyModel from 'app/components/v2/EditDataClassModal';
import { Container, CustomIconButton } from './style';
import { setWithoutTemplate } from 'app/store/features/privacyPersistedSlice';
import { ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';
import Board from './board';
import { Buttons, WhiteSpace } from 'app/constants/sizing';

const Privacy = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [beingEdited, setBeingEdited] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: '',
  });
  const textFieldRef = useRef(null);
  const commentRef = useRef(null);
  const { t } = useTranslation('translation', { keyPrefix: 'policy_manager' });
  const dispatch = useDispatch();
  const location = useLocation();

  const action = location.state?.action;

  const isViewPage = action === 'VIEW' || action === 'VIEW POLICY';
  const withoutTemplate = useSelector(state => state.privacyPersisted.WithoutTemplate);

  const navigate = useNavigate();
  const loading = useSelector(state => state.privacy.loading);

  const resetToast = () => {
    setToast({
      open: false,
      message: '',
      severity: '',
    });
  };

  const handleSaveClick = async () => {
    const name = textFieldRef.current.value;
    const comment = commentRef.current.value;
    if (!name) {
      setToast({
        open: true,
        message: t('no_policy_name_error'),
        severity: 'error',
      });
      return;
    }
    const payload = { ...withoutTemplate, name, comment };
    try {
      await apiService.savePolicyNameAndComment({
        id: withoutTemplate?.policyType,
        data: payload,
      });
      dispatch(setWithoutTemplate(payload));
      setBeingEdited(false);
    } catch ({ response }) {
      setToast({
        open: true,
        message: response.data.hint,
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  return (
    <Container sx={{ backgroundColor: ColorsV2.primary }}>
      <Box sx={{ backgroundColor: ColorsV2.primary, borderRadius: '4px' }}>
        <Toast
          open={toast.open}
          setOpen={resetToast}
          message={toast.message}
          severity={toast.severity}
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
          <div>
            {isViewPage ? (
              <CustomIconButton
                onClick={() => {
                  navigate(-1);
                }}
              >
                <BackIcon
                  sx={{
                    color: ColorsV2.white,
                    fontSize: '0.8em',
                  }}
                />
              </CustomIconButton>
            ) : (
              ''
            )}
          </div>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='h4' sx={{ color: ColorsV2.white }}>
              {t('kanban_board_header')}{isViewPage ? t('view_only') : ''}
            </Typography>
          </Box>
        </Box>
        {!isViewPage && (
          <>
            <Divider sx={{ bgcolor: ColorsV2.white, my: '0.8em' }} />
            <div style={{ color: ColorsV2.textInfo, fontFamily: 'DM Sans,sans-serif', fontSize: '0.75em' }}>
              <Typography variant='body1'>
                {t('kanban_first_description')}
              </Typography>
              <Typography variant='body1' sx={{marginY: WhiteSpace.small}}>
                {t('kanban_template_description')}
              </Typography>
              <Typography variant='body1'>
                {t('kanban_second_description')}
              </Typography>
            </div>
          </>
        )}
        <Divider sx={{ bgcolor: ColorsV2.white, mt: '10px', mb: '2em' }} />

        <Stack sx={{
          color: ColorsV2.white,
          backgroundColor: ColorsV2.secondary,
          border: `2px solid ${ColorsV2.outline}`,
          borderRadius: '4px',
          padding: '10px',
        }}>
          <Box
            sx={{
              // border: `2px solid ${ColorsV2.outline}`,
              borderRadius: '0px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              paddingBottom: 2,
              paddingLeft: 1,
            }}
          >
          <Stack direction='column' alignItems='flex-start' justifyContent='flex-start'>
            <Typography variant='h6' sx={{ color: ColorsV2.white}}>{t('name')}</Typography>
            <TextField
            defaultValue={withoutTemplate?.name}
            disabled={isViewPage || !beingEdited}
            required
            inputRef={textFieldRef}
            variant='outlined'
            placeholder='Policy Name'
            name='name'
            size='large'
            sx={{
              ...Buttons.baseStyle.textFieldLowPriority,
              "& .MuiInputBase-root": {
                height: '60px',
              },
              "& .MuiInputBase-root.Mui-disabled .MuiInputBase-input": {
                color: ColorsV2.textInfo,
                WebkitTextFillColor: ColorsV2.textInfo, // Fix for Safari/Chrome
              },
            }}
            />
          </Stack>
          <Stack direction='column' alignItems='flex-start' justifyContent='flex-start'>
            <Typography variant='h6' sx={{ color: ColorsV2.white}}>{t('description')}</Typography>
            <TextField
            defaultValue={withoutTemplate?.comment}
            disabled={isViewPage || !beingEdited}
            inputRef={commentRef}
            variant='outlined'
            name='comment'
            placeholder='Comments'
            size='large'
            multiline
            minRows={2}
            maxRows={2}
            sx={{
              ...Buttons.baseStyle.textFieldLowPriority,
              "& .MuiInputBase-root": {
                height: '60px',
              },
              "& .MuiInputBase-root.Mui-disabled .MuiInputBase-input": {
                color: ColorsV2.textInfo,
                WebkitTextFillColor: ColorsV2.textInfo, // Fix for Safari/Chrome
              },
            }}
            />  
          </Stack>
          {!isViewPage ? (
              <Grid item xs={2} sx={{ border: 0, color: ColorsV2.white, alignSelf: 'flex-end'}}>
                  {beingEdited ? (
                    <Button
                      name={'IconButton'}
                      startIcon={<SaveIcon sx={{ color: ColorsV2.white, fontSize: '0.5em' }} />}
                      onClick={handleSaveClick}
                      sx={{ ...Buttons.baseStyle.primaryButtonHighPriority, minWidth: '100px', height: '40px' }}
                    >
                      {t('save')}
                    </Button>
                  ) : (
                    <Button
                      name={'IconButton'}
                      startIcon={<EditIcon sx={{ color: ColorsV2.white }} size='small' />}
                      onClick={() => setBeingEdited(true)}
                      sx={{ ...Buttons.baseStyle.primaryButtonLowPriority, minWidth: '100px', height: '40px' }}
                    >
                      {t('edit')}
                    </Button>
                  )}
              </Grid>
            ) : (
              ''
            )}
          </Box>
          <Board beingEdited={beingEdited} />
        </Stack>
      </Box>

      {isOpen && <PrivacyModel open={isOpen} setIsOpen={setIsOpen} />}
    </Container>
  );
};

export default Privacy;
